/**
 * Free World Data Cache
 * 
 * Centralized cache with TTL and in-flight deduplication for FreeWorld data:
 * - shop_items (static catalog)
 * - market_prices (investment prices)
 * - freeworld_questions (questions list)
 * - time_periods (unified source)
 * - task_progress_data (per player+period)
 * 
 * This prevents duplicate network requests when multiple components
 * mount simultaneously and call the same fetch function.
 */

import { supabase } from '@/integrations/supabase/client';
import { SessionManager } from '@/services/sessionManager';
import type { Tables } from '@/integrations/supabase/types';

type TimePeriod = Tables<'time_periods'>;

// Cache TTLs
const CACHE_TTL_LONG = 10 * 60 * 1000;  // 10 minutes for static data (shop_items, time_periods)
const CACHE_TTL_SHORT = 60 * 1000;      // 60 seconds for market data
const CACHE_TTL_PROGRESS = 30 * 1000;   // 30 seconds for player progress data

// DEV diagnostics
let networkHits: Record<string, number> = {};

function recordHit(endpoint: string) {
  networkHits[endpoint] = (networkHits[endpoint] || 0) + 1;
  if (import.meta.env.DEV) {
    console.log(`[fwCache] 📡 Network hit: ${endpoint} (total: ${networkHits[endpoint]})`);
  }
}

export function getFWCacheStats() {
  return { ...networkHits };
}

export function resetFWCacheStats() {
  networkHits = {};
  // Reset session flags on new page view
  if (typeof window !== 'undefined' && window.__fwSessionFlags__) {
    window.__fwSessionFlags__.taskProgressLoaded.clear();
  }
}

/**
 * Log a summary of all FreeWorld network calls (for debugging duplicates)
 * Call this after page load to verify deduplication is working
 */
export function logFWCallsSummary(context: string = 'FreeWorld'): void {
  if (!import.meta.env.DEV) return;
  
  const stats = getFWCacheStats();
  const total = Object.values(stats).reduce((a, b) => a + b, 0);
  
  console.groupCollapsed(`[fwCache] 📊 ${context} Network Summary (${total} total calls)`);
  Object.entries(stats).forEach(([endpoint, count]) => {
    const status = count === 1 ? '✅' : count === 0 ? '⏭️' : '⚠️';
    console.log(`${status} ${endpoint}: ${count}`);
  });
  console.log('Target: each endpoint should be 0 (cache) or 1 (single fetch)');
  console.groupEnd();
}

// ============= SESSION-LEVEL DEDUP FOR FREEWORLD PAGE =============
// Tracks whether we've successfully loaded data this browser session
// Prevents duplicate fetches on page reload within same tab session
interface SessionLoadFlags {
  timePeriodsLoaded: boolean;
  taskProgressLoaded: Map<string, number>; // key -> timestamp
}

declare global {
  interface Window {
    __fwSessionFlags__?: SessionLoadFlags;
  }
}

function getSessionFlags(): SessionLoadFlags {
  if (typeof window === 'undefined') {
    return { timePeriodsLoaded: false, taskProgressLoaded: new Map() };
  }
  if (!window.__fwSessionFlags__) {
    window.__fwSessionFlags__ = { 
      timePeriodsLoaded: false, 
      taskProgressLoaded: new Map() 
    };
  }
  return window.__fwSessionFlags__;
}

// Session-level cooldown for task progress (5 seconds)
const SESSION_TASK_COOLDOWN_MS = 5000;

// ============= TIME PERIODS (unified source) =============

interface TimePeriodsCache {
  data: TimePeriod[];
  at: number;
}

let timePeriodsCache: TimePeriodsCache | null = null;
let timePeriodsInFlight: Promise<TimePeriod[]> | null = null;

function isTimePeriodsValid(): boolean {
  if (!timePeriodsCache) return false;
  return Date.now() - timePeriodsCache.at < CACHE_TTL_LONG;
}
/**
 * Load all time periods (unified source - replaces freeworldBootstrapStore.loadTimePeriods)
 */
export async function loadTimePeriodsUnified(options?: {
  force?: boolean;
  reason?: string;
}): Promise<TimePeriod[]> {
  const { force = false, reason = 'unknown' } = options || {};

  // Cache hit
  if (!force && timePeriodsCache && isTimePeriodsValid()) {
    if (import.meta.env.DEV) {
      console.log('[fwCache] time_periods CACHE HIT', { 
        ageMs: Date.now() - timePeriodsCache.at,
        reason,
        count: timePeriodsCache.data.length
      });
    }
    return timePeriodsCache.data;
  }

  // In-flight dedup
  if (timePeriodsInFlight) {
    if (import.meta.env.DEV) {
      console.log('[fwCache] time_periods IN-FLIGHT REUSED', { reason });
    }
    return timePeriodsInFlight;
  }

  // Network fetch
  recordHit('time_periods');
  const start = Date.now();

  timePeriodsInFlight = (async (): Promise<TimePeriod[]> => {
    try {
      const { data, error } = await supabase
        .from('time_periods')
        .select('*')
        .order('period_order', { ascending: true });

      if (error) {
        console.error('[fwCache] time_periods fetch error:', error);
        return timePeriodsCache?.data || [];
      }

      const periods = (data || []) as TimePeriod[];
      
      if (import.meta.env.DEV) {
        console.log('[fwCache] time_periods FETCHED', { 
          ms: Date.now() - start,
          reason,
          count: periods.length
        });
      }

      timePeriodsCache = { data: periods, at: Date.now() };
      return periods;
    } catch (err) {
      console.error('[fwCache] time_periods exception:', err);
      return timePeriodsCache?.data || [];
    } finally {
      timePeriodsInFlight = null;
    }
  })();

  return timePeriodsInFlight;
}

export function getCachedTimePeriodsUnified(): TimePeriod[] | null {
  if (timePeriodsCache && isTimePeriodsValid()) {
    return timePeriodsCache.data;
  }
  return null;
}

export function invalidateTimePeriodsUnified(): void {
  timePeriodsCache = null;
  if (import.meta.env.DEV) {
    console.log('[fwCache] time_periods cache invalidated');
  }
}

// ============= SHOP ITEMS =============

export interface ShopItemCached {
  id: string;
  name: string;
  description?: string;
  category: 'investment' | 'avatar_accessory' | 'collectible' | 'vehicle';
  base_price: number;
  current_price: number;
  location_id: string;
  image_url?: string;
  is_active: boolean;
  generates_income: boolean;
  income_rate: number;
  maintenance_cost: number;
  maintenance_frequency_days: number;
}

interface CacheEntry<T> {
  data: T;
  at: number;
}

let shopItemsCache: CacheEntry<ShopItemCached[]> | null = null;
let shopItemsInFlight: Promise<ShopItemCached[]> | null = null;

function isShopItemsValid(): boolean {
  if (!shopItemsCache) return false;
  return Date.now() - shopItemsCache.at < CACHE_TTL_LONG;
}

export async function loadShopItemsCached(options?: {
  force?: boolean;
  reason?: string;
}): Promise<ShopItemCached[]> {
  const { force = false, reason = 'unknown' } = options || {};

  // Cache hit
  if (!force && shopItemsCache && isShopItemsValid()) {
    if (import.meta.env.DEV) {
      console.log('[fwCache] shop_items CACHE HIT', { 
        ageMs: Date.now() - shopItemsCache.at,
        reason,
        count: shopItemsCache.data.length
      });
    }
    return shopItemsCache.data;
  }

  // In-flight dedup
  if (shopItemsInFlight) {
    if (import.meta.env.DEV) {
      console.log('[fwCache] shop_items IN-FLIGHT REUSED', { reason });
    }
    return shopItemsInFlight;
  }

  // Network fetch
  recordHit('shop_items');
  const start = Date.now();

  shopItemsInFlight = (async (): Promise<ShopItemCached[]> => {
    try {
      const { data, error } = await supabase
        .from('shop_items')
        .select('id, name, description, category, base_price, current_price, location_id, image_url, is_active, generates_income, income_rate, maintenance_cost, maintenance_frequency_days')
        .eq('is_active', true)
        .order('category', { ascending: true });

      if (error) {
        console.error('[fwCache] shop_items fetch error:', error);
        return shopItemsCache?.data || [];
      }

      const items = (data || []) as ShopItemCached[];
      
      if (import.meta.env.DEV) {
        console.log('[fwCache] shop_items FETCHED', { 
          ms: Date.now() - start,
          reason,
          count: items.length
        });
      }

      shopItemsCache = { data: items, at: Date.now() };
      return items;
    } catch (err) {
      console.error('[fwCache] shop_items exception:', err);
      return shopItemsCache?.data || [];
    } finally {
      shopItemsInFlight = null;
    }
  })();

  return shopItemsInFlight;
}

export function getCachedShopItems(): ShopItemCached[] | null {
  if (shopItemsCache && isShopItemsValid()) {
    return shopItemsCache.data;
  }
  return null;
}

// ============= MARKET PRICES =============

export interface MarketPriceCached {
  asset_id: string;
  base_price: number;
  current_price: number;
  volatility: number;
  drift: number;
  updated_at: string;
}

let marketPricesCache: CacheEntry<Map<string, MarketPriceCached>> | null = null;
let marketPricesInFlight: Promise<Map<string, MarketPriceCached>> | null = null;

function isMarketPricesValid(): boolean {
  if (!marketPricesCache) return false;
  return Date.now() - marketPricesCache.at < CACHE_TTL_SHORT;
}

export async function loadMarketPricesCached(options?: {
  force?: boolean;
  reason?: string;
}): Promise<Map<string, MarketPriceCached>> {
  const { force = false, reason = 'unknown' } = options || {};

  // Cache hit
  if (!force && marketPricesCache && isMarketPricesValid()) {
    if (import.meta.env.DEV) {
      console.log('[fwCache] market_prices CACHE HIT', { 
        ageMs: Date.now() - marketPricesCache.at,
        reason,
        count: marketPricesCache.data.size
      });
    }
    return marketPricesCache.data;
  }

  // In-flight dedup
  if (marketPricesInFlight) {
    if (import.meta.env.DEV) {
      console.log('[fwCache] market_prices IN-FLIGHT REUSED', { reason });
    }
    return marketPricesInFlight;
  }

  // Network fetch
  recordHit('market_prices');
  const start = Date.now();

  marketPricesInFlight = (async (): Promise<Map<string, MarketPriceCached>> => {
    try {
      const { data, error } = await supabase
        .from('market_prices')
        .select('asset_id, base_price, current_price, volatility, drift, updated_at');

      if (error) {
        console.error('[fwCache] market_prices fetch error:', error);
        return marketPricesCache?.data || new Map();
      }

      const pricesMap = new Map<string, MarketPriceCached>();
      (data || []).forEach((row: MarketPriceCached) => {
        pricesMap.set(row.asset_id, row);
      });

      if (import.meta.env.DEV) {
        console.log('[fwCache] market_prices FETCHED', { 
          ms: Date.now() - start,
          reason,
          count: pricesMap.size
        });
      }

      marketPricesCache = { data: pricesMap, at: Date.now() };
      return pricesMap;
    } catch (err) {
      console.error('[fwCache] market_prices exception:', err);
      return marketPricesCache?.data || new Map();
    } finally {
      marketPricesInFlight = null;
    }
  })();

  return marketPricesInFlight;
}

export function getCachedMarketPrices(): Map<string, MarketPriceCached> | null {
  if (marketPricesCache && isMarketPricesValid()) {
    return marketPricesCache.data;
  }
  return null;
}

// ============= FREEWORLD QUESTIONS (lightweight list) =============

export interface FWQuestionRow {
  id: string;
  location_id: string;
  time_period_id: string;
  parent_question_id: string | null;
  tree_status: string;
  is_active: boolean;
  status: string;
}

interface QuestionsCache {
  data: FWQuestionRow[];
  at: number;
}

let fwQuestionsCache: QuestionsCache | null = null;
let fwQuestionsInFlight: Promise<FWQuestionRow[]> | null = null;

function isFWQuestionsValid(): boolean {
  if (!fwQuestionsCache) return false;
  return Date.now() - fwQuestionsCache.at < CACHE_TTL_SHORT;
}

/**
 * Load lightweight question list (for location status, progress calculation)
 * Does NOT load full question content (story, options, etc.)
 */
export async function loadFWQuestionsListCached(options?: {
  force?: boolean;
  reason?: string;
}): Promise<FWQuestionRow[]> {
  const { force = false, reason = 'unknown' } = options || {};

  // Cache hit
  if (!force && fwQuestionsCache && isFWQuestionsValid()) {
    if (import.meta.env.DEV) {
      console.log('[fwCache] freeworld_questions CACHE HIT', { 
        ageMs: Date.now() - fwQuestionsCache.at,
        reason,
        count: fwQuestionsCache.data.length
      });
    }
    return fwQuestionsCache.data;
  }

  // In-flight dedup
  if (fwQuestionsInFlight) {
    if (import.meta.env.DEV) {
      console.log('[fwCache] freeworld_questions IN-FLIGHT REUSED', { reason });
    }
    return fwQuestionsInFlight;
  }

  // Network fetch - lightweight columns only
  recordHit('freeworld_questions');
  const start = Date.now();

  fwQuestionsInFlight = (async (): Promise<FWQuestionRow[]> => {
    try {
      // Use public VIEW for player-facing queries (no correct_index, explanation)
      const { data, error } = await (supabase as any)
        .from('freeworld_questions_public')
        .select('id, location_id, time_period_id, parent_question_id, tree_status, is_active, status');

      if (error) {
        console.error('[fwCache] freeworld_questions fetch error:', error);
        return fwQuestionsCache?.data || [];
      }

      const questions = (data || []) as unknown as FWQuestionRow[];

      if (import.meta.env.DEV) {
        console.log('[fwCache] freeworld_questions FETCHED', { 
          ms: Date.now() - start,
          reason,
          count: questions.length
        });
      }

      fwQuestionsCache = { data: questions, at: Date.now() };
      return questions;
    } catch (err) {
      console.error('[fwCache] freeworld_questions exception:', err);
      return fwQuestionsCache?.data || [];
    } finally {
      fwQuestionsInFlight = null;
    }
  })();

  return fwQuestionsInFlight;
}

export function getCachedFWQuestions(): FWQuestionRow[] | null {
  if (fwQuestionsCache && isFWQuestionsValid()) {
    return fwQuestionsCache.data;
  }
  return null;
}

/**
 * Get questions filtered by time period from cache (no fetch)
 * Returns null if cache is not valid
 */
export function getCachedFWQuestionsByPeriod(timePeriodId: string): FWQuestionRow[] | null {
  const questions = getCachedFWQuestions();
  if (!questions) return null;
  return questions.filter(q => q.time_period_id === timePeriodId);
}

/**
 * Get questions filtered by time period and location from cache (no fetch)
 */
export function getCachedFWQuestionsByPeriodAndLocation(
  timePeriodId: string, 
  locationId: string
): FWQuestionRow[] | null {
  const questions = getCachedFWQuestions();
  if (!questions) return null;
  return questions.filter(q => 
    q.time_period_id === timePeriodId && 
    q.location_id === locationId &&
    q.is_active
  );
}

// ============= FULL FREEWORLD QUESTIONS (for gameplay/admin) =============
// NOTE: Player flow should use step-by-step loading via useStepByStepQuestionLoader
// This full cache is kept for admin/teacher tooling and backward compatibility

interface FullQuestionsCache {
  data: any[];
  at: number;
}

let fullQuestionsCache: FullQuestionsCache | null = null;
let fullQuestionsInFlight: Promise<any[]> | null = null;

const CACHE_TTL_FULL_QUESTIONS = 2 * 60 * 1000; // 2 minutes for full questions

function isFullQuestionsValid(): boolean {
  if (!fullQuestionsCache) return false;
  return Date.now() - fullQuestionsCache.at < CACHE_TTL_FULL_QUESTIONS;
}

// ============= STEP-BY-STEP QUESTION LOADING (for player flow) =============
// These functions load questions one at a time via RPC

// ============= UNIFIED ENTER LOCATION RPC =============
// Single RPC that replaces: get_most_recent_freeworld_answer_with_session, 
//                           get_freeworld_root_question_public_v1, 
//                           get_freeworld_question_row_count_public_v1

export interface EnterLocationResponse {
  success: boolean;
  error?: string;
  question: {
    id: string;
    scenario_id: string;
    location_id: string;
    time_period_id: string;
    story: string;
    question: string | null;
    question_type: string | null;
    difficulty: string | null;
    points: number | null;
    tree_status: string | null;
    is_active: boolean | null;
    status: string | null;
    options: Array<{ text: string; followUpQuestionId: string | null; moneyImpact?: number }>;
  } | null;
  row_count: number;
  path_complete: boolean;
  needs_generation?: boolean;
  resume_info: {
    found: boolean;
    question_id?: string;
    selected_answer_index?: number;
    is_path_complete?: boolean;
    answered_tree_status?: string;
    broken_followup?: boolean;
  };
  server_ms: number;
}

// In-flight deduplication for enter location calls
let enterLocationInFlight: Map<string, Promise<EnterLocationResponse>> = new Map();

/**
 * Unified "enter location" RPC - single roundtrip for location entry
 * Replaces 3+ separate calls with one optimized query
 */
export async function enterFreeworldLocation(
  sessionToken: string,
  playerId: string,
  locationId: string,
  timePeriodId: string
): Promise<EnterLocationResponse> {
  const cacheKey = `enter:${playerId}:${locationId}:${timePeriodId}`;
  
  // Dev-only call counter
  if (import.meta.env.DEV) {
    console.count('fw:enter_location');
  }
  
  // In-flight dedup
  const existing = enterLocationInFlight.get(cacheKey);
  if (existing) {
    if (import.meta.env.DEV) {
      console.log('[fwCache] enter_location IN-FLIGHT REUSED', { locationId, timePeriodId });
    }
    return existing;
  }
  
  recordHit('enter_freeworld_location');
  const clientStart = Date.now();
  
  const request = (async (): Promise<EnterLocationResponse> => {
    try {
      const { data, error } = await supabase.rpc('enter_freeworld_location_public_v1', {
        p_session_token: sessionToken,
        p_player_id: playerId,
        p_location_id: locationId,
        p_time_period_id: timePeriodId
      });
      
      if (error) {
        console.error('[fwCache] enter_freeworld_location RPC error:', error);
        return {
          success: false,
          error: error.message,
          question: null,
          row_count: 0,
          path_complete: false,
          resume_info: { found: false },
          server_ms: 0
        };
      }
      
      const response = data as unknown as EnterLocationResponse;
      
      if (import.meta.env.DEV) {
        console.log('[fwCache] enter_location SUCCESS', {
          clientMs: Date.now() - clientStart,
          serverMs: response.server_ms,
          hasQuestion: !!response.question,
          pathComplete: response.path_complete,
          rowCount: response.row_count,
          resumeFound: response.resume_info?.found
        });
      }
      
      return response;
    } catch (err) {
      console.error('[fwCache] enter_location exception:', err);
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Unknown error',
        question: null,
        row_count: 0,
        path_complete: false,
        resume_info: { found: false },
        server_ms: 0
      };
    } finally {
      enterLocationInFlight.delete(cacheKey);
    }
  })();
  
  enterLocationInFlight.set(cacheKey, request);
  return request;
}

/**
 * Fetch the root question for a location + time period (LEGACY - use enterFreeworldLocation instead)
 * Returns a single sanitized question or null if none exists
 * @deprecated Use enterFreeworldLocation for new code
 */
export async function fetchRootQuestionRPC(
  locationId: string,
  timePeriodId: string
): Promise<any | null> {
  recordHit('freeworld_root_question');
  const start = Date.now();
  
  try {
    const { data, error } = await supabase.rpc('get_freeworld_root_question_public_v1', {
      p_location_id: locationId,
      p_time_period_id: timePeriodId
    });
    
    if (error) {
      console.error('[fwCache] get_freeworld_root_question_public_v1 error:', error);
      return null;
    }
    
    if (!data || data.length === 0) {
      if (import.meta.env.DEV) {
        console.log('[fwCache] No root question found', { locationId, timePeriodId });
      }
      return null;
    }
    
    const question = data[0];
    
    if (import.meta.env.DEV) {
      console.log('[fwCache] Root question fetched', { 
        ms: Date.now() - start,
        questionId: question.id
      });
    }
    
    // Map options to enhanced_options for compatibility
    return {
      ...question,
      enhanced_options: question.options
    };
  } catch (err) {
    console.error('[fwCache] fetchRootQuestionRPC exception:', err);
    return null;
  }
}

/**
 * Fetch a single question by ID
 * Returns a sanitized question or null if not found/inactive
 */
export async function fetchQuestionByIdRPC(questionId: string): Promise<any | null> {
  recordHit('freeworld_question_by_id');
  const start = Date.now();
  
  try {
    const { data, error } = await supabase.rpc('get_freeworld_question_by_id_public_v1', {
      p_question_id: questionId
    });
    
    if (error) {
      console.error('[fwCache] get_freeworld_question_by_id_public_v1 error:', error);
      return null;
    }
    
    if (!data || data.length === 0) {
      if (import.meta.env.DEV) {
        console.log('[fwCache] Question not found by ID', { questionId });
      }
      return null;
    }
    
    const question = data[0];
    
    if (import.meta.env.DEV) {
      console.log('[fwCache] Question fetched by ID', { 
        ms: Date.now() - start,
        questionId: question.id
      });
    }
    
    // Map options to enhanced_options for compatibility
    return {
      ...question,
      enhanced_options: question.options
    };
  } catch (err) {
    console.error('[fwCache] fetchQuestionByIdRPC exception:', err);
    return null;
  }
}

/**
 * Load full question data (for gameplay, includes story, options, etc.)
 * 
 * @deprecated ARCHITECTURE WARNING: This function should NOT be called from map/landing views.
 * Only allowed for admin/teacher views (edgeFunctionUtils, question editor).
 * Player flows should use enterFreeworldLocation() exclusively.
 * 
 * If you see console.count('fw:get_freeworld_questions_public_v1') on map load,
 * there's a bug - player code should never call this.
 */
export async function loadFullQuestionsCached(options?: {
  force?: boolean;
  reason?: string;
}): Promise<any[]> {
  const { force = false, reason = 'unknown' } = options || {};
  
  // ARCHITECTURE: Log warning if called from non-admin context
  if (import.meta.env.DEV) {
    console.count('fw:get_freeworld_questions_public_v1');
    if (!reason.includes('admin') && !reason.includes('edge') && !reason.includes('Editor')) {
      console.warn('[fwCache] ⚠️ loadFullQuestionsCached called from non-admin context!', { reason });
    }
  }

  // Cache hit
  if (!force && fullQuestionsCache && isFullQuestionsValid()) {
    if (import.meta.env.DEV) {
      console.log('[fwCache] freeworld_questions_full CACHE HIT', { 
        ageMs: Date.now() - fullQuestionsCache.at,
        reason,
        count: fullQuestionsCache.data.length
      });
    }
    return fullQuestionsCache.data;
  }

  // In-flight dedup
  if (fullQuestionsInFlight) {
    if (import.meta.env.DEV) {
      console.log('[fwCache] freeworld_questions_full IN-FLIGHT REUSED', { reason });
    }
    return fullQuestionsInFlight;
  }

  // Network fetch - full data
  recordHit('freeworld_questions_full');
  const start = Date.now();

  fullQuestionsInFlight = (async (): Promise<any[]> => {
    try {
      // Use player-safe RPC instead of view/table (bypasses RLS via SECURITY DEFINER)
      const { data, error } = await supabase.rpc('get_freeworld_questions_public_v1', {
        p_location_id: null,
        p_time_period_id: null,
        p_scenario_id: null
      });

      if (error) {
        // Surface exact error for debugging RLS/permission issues
        console.error('[fwCache] get_freeworld_questions_public_v1 RPC error:', {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint,
          rpc: 'get_freeworld_questions_public_v1'
        });
        
        // Log specific warning for permission errors
        if (error.code === '42501' || error.code === 'PGRST301' || error.message?.includes('permission')) {
          console.warn('[fwCache] ⚠️ Permission denied - check GRANT EXECUTE on get_freeworld_questions_public_v1');
        }
        
        return fullQuestionsCache?.data || [];
      }

      // Map RPC's "options" field to "enhanced_options" for compatibility
      const questions = (data || []).map((row: any) => ({
        ...row,
        enhanced_options: row.options
      }));

      if (import.meta.env.DEV) {
        console.log('[fwCache] freeworld_questions_full FETCHED via RPC', { 
          ms: Date.now() - start,
          reason,
          count: questions.length
        });
      }

      fullQuestionsCache = { data: questions, at: Date.now() };
      return questions;
    } catch (err) {
      console.error('[fwCache] freeworld_questions_full exception:', err);
      return fullQuestionsCache?.data || [];
    } finally {
      fullQuestionsInFlight = null;
    }
  })();

  return fullQuestionsInFlight;
}

export function invalidateFullQuestionsCache(): void {
  fullQuestionsCache = null;
  if (import.meta.env.DEV) {
    console.log('[fwCache] freeworld_questions_full cache invalidated');
  }
}

// ============= TASK PROGRESS DATA (per player + period) =============

interface TaskProgressData {
  financial_status: any;
  questions: any[];
  answers: any[];
  rows: any[];
}

interface TaskProgressCache {
  data: TaskProgressData;
  at: number;
}

// Key format: playerId:timePeriodId
const taskProgressCache = new Map<string, TaskProgressCache>();
const taskProgressInFlight = new Map<string, Promise<TaskProgressData | null>>();

function makeTaskProgressKey(playerId: string, timePeriodId: string): string {
  return `${playerId}:${timePeriodId}`;
}

function isTaskProgressValid(entry: TaskProgressCache | undefined): boolean {
  if (!entry) return false;
  return Date.now() - entry.at < CACHE_TTL_PROGRESS;
}

/**
 * Load task progress data (unified source for useTaskProgress, useUserAnsweredQuestions, useLocationStatus)
 * Uses get_freeworld_task_progress_data RPC
 */
export async function loadTaskProgressDataCached(options: {
  playerId: string;
  timePeriodId: string;
  sessionToken: string;
  force?: boolean;
  reason?: string;
}): Promise<TaskProgressData | null> {
  const { playerId, timePeriodId, sessionToken, force = false, reason = 'unknown' } = options;
  const key = makeTaskProgressKey(playerId, timePeriodId);

  // SESSION-LEVEL DEDUP: Prevent multiple hooks calling this on same page load
  // Uses a 5-second cooldown per key to allow re-fetch after events
  const sessionFlags = getSessionFlags();
  const lastSessionLoad = sessionFlags.taskProgressLoaded.get(key);
  const now = Date.now();
  
  if (!force && lastSessionLoad && (now - lastSessionLoad) < SESSION_TASK_COOLDOWN_MS) {
    // Within session cooldown - check cache instead of fetching
    const cached = taskProgressCache.get(key);
    if (cached) {
      if (import.meta.env.DEV) {
        console.log('[fwCache] task_progress_data SESSION COOLDOWN', { 
          key,
          reason,
          cooldownRemaining: SESSION_TASK_COOLDOWN_MS - (now - lastSessionLoad)
        });
      }
      return cached.data;
    }
  }

  // Cache hit
  const cached = taskProgressCache.get(key);
  if (!force && isTaskProgressValid(cached)) {
    if (import.meta.env.DEV) {
      console.log('[fwCache] task_progress_data CACHE HIT', { 
        key,
        ageMs: Date.now() - cached!.at,
        reason
      });
    }
    return cached!.data;
  }

  // In-flight dedup
  const inFlight = taskProgressInFlight.get(key);
  if (inFlight) {
    if (import.meta.env.DEV) {
      console.log('[fwCache] task_progress_data IN-FLIGHT REUSED', { key, reason });
    }
    return inFlight;
  }

  // Network fetch
  recordHit('get_freeworld_task_progress_data');
  const start = Date.now();

  const promise = (async (): Promise<TaskProgressData | null> => {
    try {
      const result = await SessionManager.withSessionRetry(async (session) => {
        const { data, error } = await supabase.rpc('get_freeworld_task_progress_data', {
          p_session_token: session.sessionToken,
          p_player_id: playerId,
          p_time_period_id: timePeriodId
        });
        
        if (error) throw error;
        return data;
      });

      if (!result) {
        if (import.meta.env.DEV) {
          console.log('[fwCache] task_progress_data empty result', { key, reason });
        }
        return null;
      }

      const progressData = result as unknown as TaskProgressData;

      if (import.meta.env.DEV) {
        console.log('[fwCache] task_progress_data FETCHED', { 
          key,
          ms: Date.now() - start,
          reason,
          answersCount: progressData.answers?.length || 0
        });
      }

      taskProgressCache.set(key, { data: progressData, at: Date.now() });
      
      // Update session flag to prevent duplicate loads from other hooks
      getSessionFlags().taskProgressLoaded.set(key, Date.now());
      
      return progressData;
    } catch (err) {
      console.error('[fwCache] task_progress_data exception:', err);
      return cached?.data || null;
    } finally {
      taskProgressInFlight.delete(key);
    }
  })();

  taskProgressInFlight.set(key, promise);
  return promise;
}

export function getCachedTaskProgressData(playerId: string, timePeriodId: string): TaskProgressData | null {
  const key = makeTaskProgressKey(playerId, timePeriodId);
  const cached = taskProgressCache.get(key);
  if (isTaskProgressValid(cached)) {
    return cached!.data;
  }
  return null;
}

export function invalidateTaskProgressCache(playerId?: string, timePeriodId?: string): void {
  const sessionFlags = getSessionFlags();
  
  if (playerId && timePeriodId) {
    const key = makeTaskProgressKey(playerId, timePeriodId);
    taskProgressCache.delete(key);
    sessionFlags.taskProgressLoaded.delete(key); // Also clear session flag
    if (import.meta.env.DEV) {
      console.log('[fwCache] task_progress_data cache invalidated', { key });
    }
  } else {
    taskProgressCache.clear();
    sessionFlags.taskProgressLoaded.clear(); // Clear all session flags
    if (import.meta.env.DEV) {
      console.log('[fwCache] task_progress_data ALL caches invalidated');
    }
  }
}

// ============= BOOTSTRAP FUNCTION =============

export interface FWBootstrapResult {
  shopItems: ShopItemCached[];
  marketPrices: Map<string, MarketPriceCached>;
  questionsList: FWQuestionRow[];
  timePeriods: TimePeriod[];
}

/**
 * Bootstrap all FreeWorld data in parallel
 * Should be called once when entering FreeWorld
 * 
 * ARCHITECTURE: On player routes, does NOT load question lists.
 * Only shop items, market prices, and time periods are loaded.
 * Question data flows through enter_freeworld_location_public_v1 only.
 */
export async function bootstrapFreeWorldData(options?: {
  force?: boolean;
  reason?: string;
  isAdminRoute?: boolean;
}): Promise<FWBootstrapResult> {
  const { force = false, reason = 'FWBootstrap' } = options || {};
  
  // Detect if we're on an admin/teacher route
  const isAdmin = options?.isAdminRoute ?? (
    typeof window !== 'undefined' && (
      window.location.pathname.includes('/admin') ||
      window.location.pathname.includes('/teacher') ||
      window.location.pathname.includes('/dashboard')
    )
  );

  if (import.meta.env.DEV) {
    console.log('[FWBootstrap] start', { force, reason, isAdmin });
    // Verify no bulk question fetch on player routes
    if (!isAdmin) {
      console.log('[FWBootstrap] ✅ Player route - skipping question list fetch');
    }
  }

  // On player routes, skip question list (architecture rule: no question data on map)
  const questionsListPromise = isAdmin 
    ? loadFWQuestionsListCached({ force, reason })
    : Promise.resolve([] as FWQuestionRow[]);

  const [shopItems, marketPrices, questionsList, timePeriods] = await Promise.all([
    loadShopItemsCached({ force, reason }),
    loadMarketPricesCached({ force, reason }),
    questionsListPromise,
    loadTimePeriodsUnified({ force, reason }),
  ]);

  if (import.meta.env.DEV) {
    console.log('[FWBootstrap] complete', {
      shopItems: shopItems.length,
      marketPrices: marketPrices.size,
      questions: questionsList.length,
      timePeriods: timePeriods.length,
      networkHits: getFWCacheStats()
    });
    
    // Log summary after a brief delay to capture all calls
    setTimeout(() => {
      logFWCallsSummary('FreeWorld Page Load');
    }, 2000);
  }

  return { shopItems, marketPrices, questionsList, timePeriods };
}

// ============= CACHE INVALIDATION =============

export function invalidateShopItemsCache(): void {
  shopItemsCache = null;
  if (import.meta.env.DEV) {
    console.log('[fwCache] shop_items cache invalidated');
  }
}

export function invalidateMarketPricesCache(): void {
  marketPricesCache = null;
  if (import.meta.env.DEV) {
    console.log('[fwCache] market_prices cache invalidated');
  }
}

export function invalidateFWQuestionsCache(): void {
  fwQuestionsCache = null;
  if (import.meta.env.DEV) {
    console.log('[fwCache] freeworld_questions cache invalidated');
  }
}

export function invalidateAllFWCache(): void {
  invalidateShopItemsCache();
  invalidateMarketPricesCache();
  invalidateFWQuestionsCache();
  invalidateFullQuestionsCache();
  invalidateTimePeriodsUnified();
  invalidateTaskProgressCache();
  if (import.meta.env.DEV) {
    console.log('[fwCache] ALL caches invalidated');
  }
}

// Expose for debugging
if (typeof window !== 'undefined') {
  (window as any).__fwCache__ = {
    getStats: getFWCacheStats,
    resetStats: resetFWCacheStats,
    invalidateAll: invalidateAllFWCache,
    getTaskProgressCache: () => {
      const result: Record<string, any> = {};
      taskProgressCache.forEach((v, k) => {
        result[k] = { ageMs: Date.now() - v.at, answersCount: v.data.answers?.length };
      });
      return result;
    }
  };
}
